import { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useContentDoc, SectionHeader, Loading, Field, PrimaryBtn, inputCls } from "./components";

const GROUPS = [
  {
    title: "Homepage",
    fields: [
      { key: "hero_kicker", label: "Small line above the title" },
      { key: "hero_title", label: "Big homepage title", area: true, hint: "Tip: press Enter to split the title onto two lines" },
      { key: "hero_subtitle", label: "Homepage description", area: true },
    ],
  },
  {
    title: "Team Information (Mission & About)",
    fields: [
      { key: "mission_title", label: "Mission heading" },
      { key: "mission_text", label: "Team description / mission", area: true },
      { key: "about_story_1", label: "About page — first paragraph", area: true },
      { key: "about_story_2", label: "About page — second paragraph", area: true },
      { key: "community_title", label: "Community section heading" },
      { key: "community_text", label: "Community section text", area: true },
    ],
  },
  {
    title: "Page Introductions",
    fields: [
      { key: "subtitle_schedule", label: "Schedule page intro", area: true },
      { key: "subtitle_roster", label: "Roster page intro", area: true },
      { key: "subtitle_coaches", label: "Coaches page intro", area: true },
      { key: "subtitle_college", label: "Bears in College page intro", area: true },
      { key: "subtitle_records", label: "Program Records page intro", area: true },
      { key: "subtitle_gallery", label: "Gallery page intro", area: true },
      { key: "subtitle_about", label: "About page intro", area: true },
      { key: "subtitle_contact", label: "Contact page intro", area: true },
    ],
  },
  {
    title: "Footer",
    fields: [{ key: "footer_text", label: "Footer description", area: true }],
  },
];

export default function TextSection() {
  const { content, setContent, save } = useContentDoc();
  const [saving, setSaving] = useState(false);

  if (!content) return <Loading />;

  const set = (k) => (e) => setContent({ ...content, [k]: e.target.value });

  const saveAll = async () => {
    setSaving(true);
    try {
      await save(content);
      toast.success("Website text saved! Changes are live on the site.");
    } catch {
      toast.error("Could not save. Please try again.");
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-text-section">
      <SectionHeader
        title="Website Text"
        subtitle="Edit the words on every page of the website. Type in the boxes, then click Save at the bottom."
      />

      <div className="space-y-6">
        {GROUPS.map((g) => (
          <div key={g.title} className="bg-white rounded-lg border border-slate-200 p-6" data-testid={`text-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h2 className="font-condensed text-2xl font-bold uppercase text-[#0F4C2A] mb-5">{g.title}</h2>
            <div className="space-y-4">
              {g.fields.map((f) => (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  {f.area ? (
                    <textarea rows={3} className={inputCls} value={content[f.key] || ""} onChange={set(f.key)} data-testid={`text-field-${f.key.replace(/_/g, "-")}`} />
                  ) : (
                    <input className={inputCls} value={content[f.key] || ""} onChange={set(f.key)} data-testid={`text-field-${f.key.replace(/_/g, "-")}`} />
                  )}
                </Field>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <PrimaryBtn onClick={saveAll} disabled={saving} className="shadow-xl px-8 py-4 text-base" data-testid="text-save-button">
          <Save size={17} /> {saving ? "Saving..." : "Save All Text"}
        </PrimaryBtn>
      </div>
    </div>
  );
}
